// BillingConfig.jsx
import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { billingService } from "@/api/billing";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import TaleButton from "@components/TaleButton";
import { TaleInput } from "@components/TaleInput";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { InternalServerError } from "@components/Errors/InternalServerError";
import { useTranslation } from "@/hooks/useTranslation";

const BillingConfig = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    // States
    const [responsePrice, setResponsePrice] = useState(0);
    const [newQuestionTier, setNewQuestionTier] = useState({ quantity: 1, price: 0 });
    const [editingQuestionTier, setEditingQuestionTier] = useState(null);
    const [editingCriteria, setEditingCriteria] = useState({});
    const [newCriteria, setNewCriteria] = useState({ label: "", price: 0 });

    // Fetch all billing data
    const {
        data: billingData,
        isLoading,
        error
    } = useQuery({
        queryKey: ["billing-config"],
        queryFn: async () => {
            const [responsePricing, questionTiers, criteria] = await Promise.all([
                 billingService.getSurveyResponsePricing(),
                billingService.getAllSurveyQuestionTiers(),
                billingService.getAllSurveyCriteria()
            ]);
            return { responsePricing, questionTiers, criteria };
        },
        retry: false,
    });

    // Update response pricing mutation
    const updateResponsePricingMutation = useMutation({
        mutationFn: (price) => billingService.updateResponsePricing(price),
        onSuccess: () => {
            queryClient.invalidateQueries(["billing-config"]);
            alert(t("billingConfig.updateResponsePriceSuccess"));
        },
        onError: (error) => {
            console.error("Update error:", error);
            alert(t("billingConfig.updateResponsePriceError"));
        },
    });

    // Add question tier mutation
    const addQuestionTierMutation = useMutation({
        mutationFn: (data) => billingService.addQuestionTier(data),
        onSuccess: () => {
            queryClient.invalidateQueries(["billing-config"]);
            setNewQuestionTier({ quantity: 1, price: 0 });
            alert(t("billingConfig.addTierSuccess"));
        },
        onError: (error) => {
            console.error("Add error:", error);
            alert(t("billingConfig.addTierError"));
        },
    });

    // Update question tier mutation
    const updateQuestionTierMutation = useMutation({
        mutationFn: ({ id, data }) => billingService.updateQuestionTier(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries(["billing-config"]);
            setEditingQuestionTier(null);
            alert(t("billingConfig.updateTierSuccess"));
        },
        onError: (error) => {
            console.error("Update error:", error);
            alert(t("billingConfig.updateTierError"));
        },
    });

    // Update criteria mutation
    const updateCriteriaMutation = useMutation({
        mutationFn: ({ id, data }) => billingService.updateSurveyCriteria(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries(["billing-config"]);
            setEditingCriteria({});
            alert(t("billingConfig.updateCriteriaSuccess"));
        },
        onError: (error) => {
            console.error("Update error:", error);
            alert(t("billingConfig.updateCriteriaError"));
        },
    });

    // Initialize form with fetched data
    useEffect(() => {
        if (billingData?.responsePricing) {
            setResponsePrice(billingData.responsePricing.pricePerResponse || 0);
        }
    }, [billingData]);

    const handleUpdateResponsePrice = () => {
        if (!responsePrice || responsePrice <= 0) {
            alert(t("billingConfig.invalidPriceError"));
            return;
        }
        updateResponsePricingMutation.mutate(parseFloat(responsePrice));
    };

    const handleAddQuestionTier = () => {
        if (!newQuestionTier.quantity || !newQuestionTier.price) {
            alert(t("billingConfig.fillAllFieldsError"));
            return;
        }
        addQuestionTierMutation.mutate({
            quantity: parseInt(newQuestionTier.quantity),
            price: parseFloat(newQuestionTier.price)
        });
    };

    const handleUpdateQuestionTier = (id, data) => {
        updateQuestionTierMutation.mutate({ id, data });
    };

    const handleUpdateCriteria = (id, data) => {
        updateCriteriaMutation.mutate({ id, data });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-TN', {
            style: 'currency',
            currency: 'TND'
        }).format(amount || 0);
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <TaleLoadingFallback />
            </div>
        );
    }

    if (error) {
        return <InternalServerError />;
    }

    const { responsePricing, questionTiers = [], criteria = [] } = billingData || {};

    return (
            <TaleDashbaordMainCard className="p-6">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">{t("billingConfig.title")}</h1>
                    <p className="text-gray-600 mt-2">
                        {t("billingConfig.subtitle")}
                    </p>
                    <hr/>
                </div>
                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-gray-50 p-6 rounded-xl border">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-sm text-blue-600 font-medium">{t("billingConfig.statPricePerResponse")}</div>
                                <div className="text-2xl font-bold text-gray-900">
                                    {formatCurrency(responsePricing?.pricePerResponse || 0)}
                                </div>
                            </div>
                            <i className="fas fa-receipt text-3xl text-blue-400"></i>
                        </div>
                        <div className="mt-3 text-sm text-gray-700">
                            {t("billingConfig.statPricePerResponseDesc")}
                        </div>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-xl border">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-sm text-green-600 font-medium">{t("billingConfig.statQuestionTiers")}</div>
                                <div className="text-2xl font-bold text-gray-900">
                                    {questionTiers.length}
                                </div>
                            </div>
                            <i className="fas fa-layer-group text-3xl text-green-400"></i>
                        </div>
                        <div className="mt-3 text-sm text-gray-700">
                            {t("billingConfig.statQuestionTiersDesc")}
                        </div>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-xl border">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-sm text-purple-600 font-medium">{t("billingConfig.statCriteria")}</div>
                                <div className="text-2xl font-bold text-gray-900">
                                    {criteria.length}
                                </div>
                            </div>
                            <i className="fas fa-filter text-3xl text-purple-400"></i>
                        </div>
                        <div className="mt-3 text-sm text-gray-700">
                            {t("billingConfig.statCriteriaDesc")}
                        </div>
                    </div>
                </div>

                {/* Main Configuration Sections */}
                <div className="space-y-8">
                    {/* Response Pricing Section */}
                    <div className="border-t pt-8">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mr-4 rtl:mr-0 rtl:ml-4">
                                <i className="fas fa-receipt text-blue-500 text-xl"></i>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">{t("billingConfig.responsePricingSectionTitle")}</h2>
                                <p className="text-gray-600 text-sm">
                                    {t("billingConfig.responsePricingSectionDesc")}
                                </p>
                            </div>
                        </div>

                        <div className="mb-6">
                            <div className="flex flex-col md:flex-row md:items-end gap-4">
                                <div className="flex-1">
                                    <TaleInput
                                        label={t("billingConfig.pricePerResponseLabel")}
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={responsePrice}
                                        onChange={(e) => setResponsePrice(e.target.value)}
                                        placeholder={t("billingConfig.pricePerResponsePlaceholder")}
                                        helperText={t("billingConfig.pricePerResponseHelper")}
                                    />
                                </div>
                                <div className="md:pb-2">
                                    <TaleButton
                                        onClick={handleUpdateResponsePrice}
                                        loading={updateResponsePricingMutation.isPending}
                                    >
                                        {t("billingConfig.updateButton")}
                                    </TaleButton>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Question Tiers Section */}
                    <div className="border-t pt-8">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center">
                                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center mr-4 rtl:mr-0 rtl:ml-4">
                                    <i className="fas fa-layer-group text-green-500 text-xl"></i>
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">{t("billingConfig.questionTiersSectionTitle")}</h2>
                                    <p className="text-gray-600 text-sm">
                                        {t("billingConfig.tiersConfiguredCount", { count: questionTiers.length })}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Add New Tier Form */}
                        <div className="mb-8 p-6 bg-gray-50 rounded-xl border">
                            <h3 className="font-semibold text-gray-900 mb-4">
                                <i className="fas fa-plus-circle text-green-500 mr-2 rtl:mr-0 rtl:ml-2"></i>
                                {t("billingConfig.addNewTierTitle")}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <TaleInput
                                    label={t("billingConfig.questionCountLabel")}
                                    type="number"
                                    min="1"
                                    value={newQuestionTier.quantity}
                                    onChange={(e) => setNewQuestionTier({
                                        ...newQuestionTier,
                                        quantity: e.target.value
                                    })}
                                    placeholder={t("billingConfig.questionCountPlaceholder")}
                                />
                                <TaleInput
                                    label={t("billingConfig.tierPriceLabel")}
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={newQuestionTier.price}
                                    onChange={(e) => setNewQuestionTier({
                                        ...newQuestionTier,
                                        price: e.target.value
                                    })}
                                    placeholder={t("billingConfig.tierPricePlaceholder")}
                                />
                                <div className="flex items-end">
                                    <TaleButton
                                        onClick={handleAddQuestionTier}
                                        loading={addQuestionTierMutation.isPending}
                                        className="w-full bg-green-600 hover:bg-green-700 border-green-600"
                                    >
                                        <i className="fas fa-plus mr-2 rtl:mr-0 rtl:ml-2"></i>
                                        {t("billingConfig.addButton")}
                                    </TaleButton>
                                </div>
                            </div>
                        </div>

                        {/* Existing Tiers Table */}
                        {questionTiers.length > 0 ? (
                            <div className="overflow-x-auto border rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t("billingConfig.columnQuestionCount")}
                                        </th>
                                        <th className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t("billingConfig.columnPrice")}
                                        </th>
                                        <th className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t("billingConfig.columnActions")}
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {questionTiers.map((tier) => (
                                        <tr key={tier.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {editingQuestionTier?.id === tier.id ? (
                                                    <div className="w-32">
                                                        <TaleInput
                                                            type="number"
                                                            min="1"
                                                            value={editingQuestionTier.quantity}
                                                            onChange={(e) => setEditingQuestionTier({
                                                                ...editingQuestionTier,
                                                                quantity: e.target.value
                                                            })}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="font-medium text-gray-900">{tier.quantity}</div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {editingQuestionTier?.id === tier.id ? (
                                                    <div className="w-32">
                                                        <TaleInput
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={editingQuestionTier.price}
                                                            onChange={(e) => setEditingQuestionTier({
                                                                ...editingQuestionTier,
                                                                price: e.target.value
                                                            })}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="font-medium text-green-600">
                                                        {formatCurrency(tier.price)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex gap-2">
                                                    {editingQuestionTier?.id === tier.id ? (
                                                        <>
                                                            <TaleButton
                                                                size="sm"
                                                                onClick={() => handleUpdateQuestionTier(tier.id, {
                                                                    quantity: parseInt(editingQuestionTier.quantity),
                                                                    price: parseFloat(editingQuestionTier.price)
                                                                })}
                                                                loading={updateQuestionTierMutation.isPending}
                                                            >
                                                                <i className="fas fa-check mr-1 rtl:mr-0 rtl:ml-1"></i>
                                                                {t("billingConfig.validateButton")}
                                                            </TaleButton>
                                                            <TaleButton
                                                                size="sm"
                                                                variant="outlined"
                                                                onClick={() => setEditingQuestionTier(null)}
                                                            >
                                                                {t("billingConfig.cancelButton")}
                                                            </TaleButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <TaleButton
                                                                size="sm"
                                                                variant="outlined"
                                                                onClick={() => setEditingQuestionTier({
                                                                    id: tier.id,
                                                                    quantity: tier.quantity,
                                                                    price: tier.price
                                                                })}
                                                            >
                                                                <i className="fas fa-edit mr-1 rtl:mr-0 rtl:ml-1"></i>
                                                                {t("billingConfig.editButton")}
                                                            </TaleButton>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-8 bg-gray-50 rounded-lg">
                                <i className="fas fa-layer-group text-4xl text-gray-300 mb-3"></i>
                                <p className="text-gray-500">{t("billingConfig.noTiersConfigured")}</p>
                                <p className="text-sm text-gray-400 mt-1">{t("billingConfig.addFirstTierHint")}</p>
                            </div>
                        )}

                        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-green-200">
                            <div className="flex items-start">
                                <i className="fas fa-lightbulb text-green-500 mt-0.5 mr-3 rtl:mr-0 rtl:ml-3"></i>
                                <div className="text-sm text-green-700">
                                    {t("billingConfig.tiersHint")}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Survey Criteria Section */}
                    <div className="border-t pt-8">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center">
                                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mr-4 rtl:mr-0 rtl:ml-4">
                                    <i className="fas fa-filter text-purple-500 text-xl"></i>
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">{t("billingConfig.criteriaSectionTitle")}</h2>
                                    <p className="text-gray-600 text-sm">
                                        {t("billingConfig.criteriaConfiguredCount", { count: criteria.length })}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Add New Criteria Form */}
                        <div className="mb-8 p-6 bg-gray-50 rounded-xl border">
                            <h3 className="font-semibold text-gray-900 mb-4">
                                <i className="fas fa-plus-circle text-purple-500 mr-2 rtl:mr-0 rtl:ml-2"></i>
                                {t("billingConfig.addNewCriteriaTitle")}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <TaleInput
                                    label={t("billingConfig.criteriaNameLabel")}
                                    value={newCriteria.label}
                                    onChange={(e) => setNewCriteria({
                                        ...newCriteria,
                                        label: e.target.value
                                    })}
                                    placeholder={t("billingConfig.criteriaNamePlaceholder")}
                                />
                                <TaleInput
                                    label={t("billingConfig.extraPriceLabel")}
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={newCriteria.price}
                                    onChange={(e) => setNewCriteria({
                                        ...newCriteria,
                                        price: e.target.value
                                    })}
                                    placeholder={t("billingConfig.extraPricePlaceholder")}
                                />
                                <div className="flex items-end">
                                    <TaleButton
                                        onClick={() => {
                                            // Implement add criteria if API exists
                                            alert(t("billingConfig.featureNotImplemented"));
                                        }}
                                        className="w-full bg-purple-600 hover:bg-purple-700 border-purple-600"
                                    >
                                        <i className="fas fa-plus mr-2 rtl:mr-0 rtl:ml-2"></i>
                                        {t("billingConfig.addButton")}
                                    </TaleButton>
                                </div>
                            </div>
                        </div>

                        {/* Existing Criteria Table */}
                        {criteria.length > 0 ? (
                            <div className="overflow-x-auto border rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t("billingConfig.columnCriteria")}
                                        </th>
                                        <th className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t("billingConfig.columnSlug")}
                                        </th>
                                        <th className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t("billingConfig.columnExtraPrice")}
                                        </th>
                                        <th className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t("billingConfig.columnActions")}
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {criteria.map((criterion) => (
                                        <tr key={criterion.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                {editingCriteria[criterion.id] ? (
                                                    <div className="w-40">
                                                        <TaleInput
                                                            value={editingCriteria[criterion.id].label}
                                                            onChange={(e) => setEditingCriteria({
                                                                ...editingCriteria,
                                                                [criterion.id]: {
                                                                    ...editingCriteria[criterion.id],
                                                                    label: e.target.value
                                                                }
                                                            })}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="font-medium text-gray-900">{criterion.label}</div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                                                    {criterion.slug}
                                                </code>
                                            </td>
                                            <td className="px-6 py-4">
                                                {editingCriteria[criterion.id] ? (
                                                    <div className="w-32">
                                                        <TaleInput
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={editingCriteria[criterion.id].price}
                                                            onChange={(e) => setEditingCriteria({
                                                                ...editingCriteria,
                                                                [criterion.id]: {
                                                                    ...editingCriteria[criterion.id],
                                                                    price: e.target.value
                                                                }
                                                            })}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="font-medium text-purple-600">
                                                        {formatCurrency(criterion.price)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex gap-2">
                                                    {editingCriteria[criterion.id] ? (
                                                        <>
                                                            <TaleButton
                                                                size="sm"
                                                                onClick={() => handleUpdateCriteria(criterion.id, {
                                                                    label: editingCriteria[criterion.id].label,
                                                                    price: parseFloat(editingCriteria[criterion.id].price)
                                                                })}
                                                                loading={updateCriteriaMutation.isPending}
                                                            >
                                                                <i className="fas fa-check mr-1 rtl:mr-0 rtl:ml-1"></i>
                                                                {t("billingConfig.validateButton")}
                                                            </TaleButton>
                                                            <TaleButton
                                                                size="sm"
                                                                variant="outlined"
                                                                onClick={() => {
                                                                    const newEditing = { ...editingCriteria };
                                                                    delete newEditing[criterion.id];
                                                                    setEditingCriteria(newEditing);
                                                                }}
                                                            >
                                                                {t("billingConfig.cancelButton")}
                                                            </TaleButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <TaleButton
                                                                size="sm"
                                                                variant="outlined"
                                                                onClick={() => setEditingCriteria({
                                                                    ...editingCriteria,
                                                                    [criterion.id]: {
                                                                        label: criterion.label,
                                                                        price: criterion.price
                                                                    }
                                                                })}
                                                            >
                                                                <i className="fas fa-edit mr-1 rtl:mr-0 rtl:ml-1"></i>
                                                                {t("billingConfig.editButton")}
                                                            </TaleButton>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-8 bg-gray-50 rounded-lg">
                                <i className="fas fa-filter text-4xl text-gray-300 mb-3"></i>
                                <p className="text-gray-500">{t("billingConfig.noCriteriaConfigured")}</p>
                                <p className="text-sm text-gray-400 mt-1">{t("billingConfig.addFirstCriteriaHint")}</p>
                            </div>
                        )}

                        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-purple-200">
                            <div className="flex items-start">
                                <i className="fas fa-lightbulb text-purple-500 mt-0.5 mr-3 rtl:mr-0 rtl:ml-3"></i>
                                <div className="text-sm text-purple-700">
                                    {t("billingConfig.criteriaHint")}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Billing Preview Section */}
                    <div className="border-t pt-8">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center mr-4 rtl:mr-0 rtl:ml-4">
                                <i className="fas fa-calculator text-orange-500 text-xl"></i>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">{t("billingConfig.simulationSectionTitle")}</h2>
                                <p className="text-gray-600 text-sm">
                                    {t("billingConfig.simulationSectionDesc")}
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <h3 className="font-semibold text-gray-900">{t("billingConfig.simulationParamsTitle")}</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-gray-50 p-4 rounded-lg border">
                                        <div className="text-sm text-gray-600 mb-1">{t("billingConfig.simResponses")}</div>
                                        <div className="text-lg font-semibold">100</div>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-lg border">
                                        <div className="text-sm text-gray-600 mb-1">{t("billingConfig.simQuestions")}</div>
                                        <div className="text-lg font-semibold">15</div>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-lg border">
                                        <div className="text-sm text-gray-600 mb-1">{t("billingConfig.simCriteria")}</div>
                                        <div className="text-lg font-semibold">2</div>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-lg border">
                                        <div className="text-sm text-gray-600 mb-1">{t("billingConfig.simTier")}</div>
                                        <div className="text-lg font-semibold">15 Q</div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 p-6 rounded-xl border">
                                <h3 className="font-semibold text-gray-900 mb-4">{t("billingConfig.calculationResultTitle")}</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">{t("billingConfig.calcResponsesLine", { price: formatCurrency(responsePricing?.pricePerResponse || 0) })}</span>
                                        <span>{formatCurrency(100 * (responsePricing?.pricePerResponse || 0))}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">{t("billingConfig.calcTierLine")}</span>
                                        <span className="text-green-600">+ {formatCurrency(25)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">{t("billingConfig.calcCriteriaLine")}</span>
                                        <span className="text-purple-600">+ {formatCurrency(30)}</span>
                                    </div>
                                    <div className="pt-3 border-t">
                                        <div className="flex justify-between items-center">
                                            <span className="font-semibold text-gray-900">{t("billingConfig.calcTotalLabel")}</span>
                                            <span className="text-2xl font-bold text-blue-600">
                                                {formatCurrency(100 * (responsePricing?.pricePerResponse || 0) + 25 + 30)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* How it Works Section */}
                    <div className="border-t pt-8">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center mr-4 rtl:mr-0 rtl:ml-4">
                                <i className="fas fa-question-circle text-gray-500 text-xl"></i>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">{t("billingConfig.howItWorksTitle")}</h2>
                                <p className="text-gray-600 text-sm">
                                    {t("billingConfig.howItWorksDesc")}
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                            <div className="p-6 bg-gray-50 rounded-xl border">
                                <div className="flex items-center mb-4">
                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                                        <i className="fas fa-receipt text-blue-500"></i>
                                    </div>
                                    <div className="font-semibold text-blue-700">{t("billingConfig.basePriceTitle")}</div>
                                </div>
                                <p className="text-sm text-gray-700">
                                    {t("billingConfig.basePriceDesc")}
                                </p>
                            </div>

                            <div className="p-6 bg-gray-50 rounded-xl border">
                                <div className="flex items-center mb-4">
                                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                                        <i className="fas fa-layer-group text-green-500"></i>
                                    </div>
                                    <div className="font-semibold text-green-700">{t("billingConfig.questionTiersSectionTitle")}</div>
                                </div>
                                <p className="text-sm text-gray-700">
                                    {t("billingConfig.tiersDesc")}
                                </p>
                            </div>

                            <div className="p-6 bg-gray-50 rounded-xl border">
                                <div className="flex items-center mb-4">
                                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                                        <i className="fas fa-filter text-purple-500"></i>
                                    </div>
                                    <div className="font-semibold text-purple-700">{t("billingConfig.extraCriteriaTitle")}</div>
                                </div>
                                <p className="text-sm text-gray-700">
                                    {t("billingConfig.extraCriteriaDesc")}
                                </p>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-xl border">
                            <div className="flex items-start">
                                <i className="fas fa-calculator text-yellow-500 text-xl mr-4 rtl:mr-0 rtl:ml-4"></i>
                                <div>
                                    <h4 className="font-bold text-yellow-800 mb-2">{t("billingConfig.formulaTitle")}</h4>
                                    <div className="text-sm text-gray-700 font-mono bg-gray-50 p-3 rounded-lg">
                                        {t("billingConfig.formulaLine1")}<br/>
                                        {t("billingConfig.formulaLine2")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </TaleDashbaordMainCard>
    );
};

export default BillingConfig;
